import { PropsWithChildren, ReactElement, useCallback, useMemo } from "react";

import { useFormControl } from "@chakra-ui/react";

import ReactSelect, {
  ActionMeta,
  components,
  createFilter,
  Props as ReactSelectProps,
  SingleValue
} from "react-select";

import {
  Option,
  PassthroughProps,
  SelectBaseProps
} from "@/components/forms/types";
import useReactSelectStyles from "@/components/theme/useReactSelectStyles";
import {
  getStringValue,
  isOptionNotOptionGroup
} from "@/hooks/form/useFormUtil";
import useSelectKeyDown from "@/hooks/util/useSelectKeyDown";

import { OptionWithSecondaryLabel } from "./OptionWithSecondaryLabel";

const defaultComponents = {
  Option: OptionWithSecondaryLabel
};

export interface SelectProps<T extends string = string>
  extends Omit<
      ReactSelectProps<Option<T>, false>,
      keyof SelectBaseProps<T> | keyof PassthroughProps
    >,
    SelectBaseProps<T>,
    PassthroughProps {
  components?: Partial<typeof components>;
  classNamePrefix?: string;
}

export function Select<T extends string = string>({
  options,
  value,
  onChange,
  inputId,
  isSearchable = false,
  searchMatchFrom = "start",
  components: customComponents,
  styles: customStyles,
  classNamePrefix,
  ...selectProps
}: PropsWithChildren<SelectProps<T>>): ReactElement<any, any> {
  const { onMenuOpen, onMenuClose, onSelectKeyDown } = useSelectKeyDown();
  const { styles, theme } = useReactSelectStyles<Option<T>>();

  // Get the ID from the Chakra `FormControl` if there is one, so that
  // the label and form field are connected.
  const { id } = useFormControl({});

  const filterOption = useMemo(
    () =>
      createFilter<Option<T>>({
        ignoreCase: true,
        ignoreAccents: true,
        matchFrom: searchMatchFrom,
        stringify: ({ data }) => getStringValue(data),
        trim: true
      }),
    [searchMatchFrom]
  );

  const selectedOption = useMemo(() => {
    // We cant use a find here, cause there might be buried options and we
    // need to return singulars, not OptionGroups
    let matchingOption: Option<T> | undefined;
    options.forEach((optionOrGroup) => {
      if (matchingOption) {
        return;
      }

      if (isOptionNotOptionGroup(optionOrGroup)) {
        if (optionOrGroup.value === value) {
          matchingOption = optionOrGroup;
        }
      } else {
        matchingOption = optionOrGroup.options.find(
          (option) => option.value === value
        );
      }
    });

    // ReactSelect expects `null` for no selection, rather than `undefined`.
    return matchingOption ?? null;
  }, [options, value]);

  const handleChange = useCallback(
    (newOption: SingleValue<Option<T>>, actionMeta: ActionMeta<Option<T>>) => {
      if (!newOption || actionMeta.action === "clear") {
        onChange(null);
      } else {
        onChange(newOption.value);
      }
    },
    [onChange]
  );

  // Merge default components with any custom components
  const mergedComponents = useMemo(
    () => ({
      ...defaultComponents,
      ...customComponents
    }),
    [customComponents]
  );

  return (
    <ReactSelect<Option<T>, false>
      filterOption={filterOption}
      inputId={inputId ?? id}
      isMulti={false}
      isSearchable={isSearchable}
      onKeyDown={onSelectKeyDown}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      options={options}
      styles={{
        ...styles,
        ...customStyles
      }}
      theme={theme}
      value={selectedOption}
      onChange={handleChange}
      components={mergedComponents}
      classNamePrefix={classNamePrefix}
      {...selectProps}
    />
  );
}
